import { LoaderFunction, LoaderFunctionArgs, redirect } from 'react-router-dom';
import { devbackendUrl } from './constants';
import {
  ActiveGameLookupError,
  ACTIVE_GAME_RESOLUTION_ERROR_KEY,
  ACTIVE_GAME_RESOLUTION_EVENT,
  clearHopGameContext,
  clearStandaloneGameContext,
  fetchActiveGameSession,
  readActiveGameContext,
  readHopGameData,
  syncContextFromActiveSession,
} from './active-game-context';
import {
  getHopGamePath,
  getStandaloneGamePath,
  getStandaloneJoinPath,
  getStoredStandaloneJoinPath,
} from './game-routes';
import { AuthUtils } from './helper_functions';

const getValidToken = () => {
  const token = AuthUtils.getAuthToken();
  return token && AuthUtils.isTokenValid(token) ? token : null;
};

const getActiveStandaloneSession = async () => {
  const token = getValidToken();
  if (!token) return null;
  const userId = AuthUtils.getAuthUserId();
  const context = readActiveGameContext();
  if (
    context?.mode === 'standalone'
    && context.userId !== null
    && userId !== null
    && context.userId !== userId
  ) {
    clearStandaloneGameContext();
  }
  const active = await fetchActiveGameSession(devbackendUrl, token);
  if (active.state === 'lobby' || active.state === 'starting' || active.state === 'playing') {
    syncContextFromActiveSession(active, userId);
  }
  return active;
};

const getActiveGamePath = (active: {
  state: 'lobby' | 'starting' | 'playing';
  sessionId: number;
  gameType: string;
  gameRoomId: number | null;
}) => {
  if (active.state === 'lobby' || !active.gameRoomId) {
    return `/game-lobby/${active.sessionId}`;
  }
  return getStandaloneGamePath(active.gameType, active.gameRoomId);
};

const getValidatedHopPath = async (requestedPath: string) => {
  const token = getValidToken();
  const hopGameData = readHopGameData(
    token ? AuthUtils.getAuthUserId() : null,
  );
  if (!hopGameData?.gameRoomId || !hopGameData?.gameType) return null;
  const storedPath = getHopGamePath(hopGameData.gameType, hopGameData.gameRoomId);

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(
      `${devbackendUrl}hop/session/${hopGameData.sessionId}/dashboard`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      if ([401, 403, 404, 410].includes(response.status)) {
        clearHopGameContext();
        return null;
      }
      sessionStorage.setItem(ACTIVE_GAME_RESOLUTION_ERROR_KEY, '1');
      window.dispatchEvent(new CustomEvent(ACTIVE_GAME_RESOLUTION_EVENT, {
        detail: { unavailable: true },
      }));
      return requestedPath === storedPath ? storedPath : null;
    }
    sessionStorage.removeItem(ACTIVE_GAME_RESOLUTION_ERROR_KEY);
    window.dispatchEvent(new CustomEvent(ACTIVE_GAME_RESOLUTION_EVENT, {
      detail: { unavailable: false },
    }));
    const dashboard = await response.json();
    const currentGame = dashboard?.session?.currentGameData;
    const isSameRunningGame = dashboard?.session?.gameState === 'ongoing'
      && Number(dashboard?.session?.id) === Number(hopGameData.sessionId)
      && Number(currentGame?.gameRoomId) === Number(hopGameData.gameRoomId)
      && currentGame?.gameType === hopGameData.gameType;
    if (!isSameRunningGame) {
      clearHopGameContext();
      return null;
    }
    return storedPath;
  } catch {
    // During an outage, preserve an already-open Hop route, but do not force
    // unrelated pages into a room that cannot currently be validated.
    sessionStorage.setItem(ACTIVE_GAME_RESOLUTION_ERROR_KEY, '1');
    window.dispatchEvent(new CustomEvent(ACTIVE_GAME_RESOLUTION_EVENT, {
      detail: { unavailable: true },
    }));
    return requestedPath === storedPath ? storedPath : null;
  } finally {
    window.clearTimeout(timeout);
  }
};

const getJoinPathForRequest = (pathname: string) => {
  if (pathname.includes('/flip-card-game/')) return getStandaloneJoinPath('flipCardGame');
  if (pathname.includes('/double-down/')) return getStandaloneJoinPath('doubleDown');
  return getStandaloneJoinPath('quizMarathon');
};

const getTerminalPath = (terminal: {
  sessionId: number;
  gameType: string;
  outcome: 'completed' | 'cancelled' | 'abandoned';
  endReason: string;
  resultsAvailable: boolean;
}) => {
  if (terminal.outcome === 'completed' && terminal.resultsAvailable) {
    return `/profile/game-session/${terminal.sessionId}`;
  }
  const joinPath = getStandaloneJoinPath(terminal.gameType);
  const params = new URLSearchParams({
    sessionEnded: terminal.endReason || terminal.outcome,
    endedSessionId: String(terminal.sessionId),
  });
  return `${joinPath}?${params.toString()}`;
};

export async function rootLoader(args: { request: Request }) {
  return activeGameRedirectLoader(args as LoaderFunctionArgs);
}

export const activeGameRedirectLoader: LoaderFunction = async ({ request }) => {
  const requestedUrl = new URL(request.url);
  const requestedPath = requestedUrl.pathname;
  const requestedLocation = `${requestedUrl.pathname}${requestedUrl.search}`;
  const hopPath = await getValidatedHopPath(requestedPath);

  // Hop state is intentionally tab-scoped. Never reinterpret a leftover Hop
  // room id as a standalone game when hopGameData is gone.
  if (hopPath) {
    return requestedPath === hopPath ? null : redirect(hopPath);
  }

  try {
    const active = await getActiveStandaloneSession();
    if (!active) return null;

    if (active.state === 'terminal') {
      clearStandaloneGameContext();
      if (sessionStorage.getItem('dismissedGameSessionTerminalId') === String(active.sessionId)) {
        return null;
      }
      const terminalPath = getTerminalPath(active);
      return requestedLocation === terminalPath ? null : redirect(terminalPath);
    }
    if (active.state === 'none') {
      clearStandaloneGameContext();
      return null;
    }

    const gamePath = getActiveGamePath(active);
    return requestedPath === gamePath ? null : redirect(gamePath);
  } catch (error) {
    if (error instanceof ActiveGameLookupError && (error.status === 401 || error.status === 403)) {
      return redirect('/login');
    }
    // Backend reachability must not turn a local hint into authority and must
    // not destroy a context that can still reconnect when the API recovers.
    return null;
  }
};

export const gamePageLoader: LoaderFunction = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const isHopMode = url.pathname.includes('/flip-card-game/hop/')
    || url.pathname.includes('/quiz-marathon/hop/')
    || url.pathname.includes('/double-down/hop/');

  if (isHopMode) {
    const canonicalHopPath = await getValidatedHopPath(url.pathname);
    if (!canonicalHopPath) {
      return redirect(getStoredStandaloneJoinPath());
    }
    return url.pathname === canonicalHopPath ? null : redirect(canonicalHopPath);
  }

  try {
    const active = await getActiveStandaloneSession();
    if (active?.state === 'starting' || active?.state === 'playing') {
      const canonicalPath = getActiveGamePath(active);
      if (url.pathname !== canonicalPath) return redirect(canonicalPath);
      return null;
    }

    if (active?.state === 'lobby') return redirect(`/game-lobby/${active.sessionId}`);
    if (active?.state === 'terminal') {
      clearStandaloneGameContext();
      return redirect(getTerminalPath(active));
    }
    if (active?.state === 'none') {
      const joinPath = getStandaloneJoinPath(
        readActiveGameContext()?.gameType,
      );
      clearStandaloneGameContext();
      return redirect(joinPath);
    }
  } catch (error) {
    if (error instanceof ActiveGameLookupError && (error.status === 401 || error.status === 403)) {
      return redirect('/login');
    }
    // An outage cannot prove this room is stale. Keep the requested route and
    // preserve the reconnect hint until the resolver is reachable again.
    return null;
  }

  const storedContext = readActiveGameContext();
  const joinPath = storedContext
    ? getStandaloneJoinPath(storedContext.gameType)
    : getJoinPathForRequest(url.pathname);
  clearStandaloneGameContext();
  return redirect(joinPath);
};

// Guard for /game-lobby/:gameId. The authenticated active-session resolver is
// authoritative; the direct session fetch is used only when no token exists.
export const gameLobbyLoader: LoaderFunction = async ({ params }: LoaderFunctionArgs) => {
  const sessionId = params.gameId;
  if (!sessionId) return redirect(getStandaloneJoinPath());

  try {
    const active = await getActiveStandaloneSession();
    if (active?.state === 'lobby' && String(active.sessionId) === String(sessionId)) return null;
    if (active?.state === 'starting' || active?.state === 'playing') {
      return redirect(getActiveGamePath(active));
    }
    if (active?.state === 'lobby') return redirect(`/game-lobby/${active.sessionId}`);
    if (active?.state === 'terminal') {
      clearStandaloneGameContext();
      return redirect(getTerminalPath(active));
    }
    if (active?.state === 'none') {
      const joinPath = getStandaloneJoinPath(
        readActiveGameContext()?.gameType,
      );
      clearStandaloneGameContext();
      return redirect(joinPath);
    }
  } catch (error) {
    if (error instanceof ActiveGameLookupError && (error.status === 401 || error.status === 403)) {
      return redirect('/login');
    }
    // An outage cannot prove this lobby is stale. Preserve the context and
    // stay put rather than accepting a weaker public lookup as authority.
    return null;
  }

  try {
    const token = getValidToken();
    const response = await fetch(`${devbackendUrl}game-session/${sessionId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      clearStandaloneGameContext();
      return redirect(getStandaloneJoinPath());
    }

    const data = await response.json();
    const status = data.status || data.session?.status;
    const gameType = data.gameType || data.session?.gameType;
    const roomId = data.gameRoomId || data.session?.gameRoomId;

    if (status === 'completed' || status === 'cancelled' || status === 'abandoned' || status === 'ended') {
      clearStandaloneGameContext();
      return redirect(getStandaloneJoinPath(gameType));
    }
    if ((status === 'starting' || status === 'playing' || status === 'ongoing'
      || status === 'in_progress' || status === 'started') && roomId) {
      return redirect(getStandaloneGamePath(gameType, roomId));
    }
    return null;
  } catch {
    // Keep the lobby visible during a transient outage; the page exposes its
    // own connection state and can recover without deleting local context.
    return null;
  }
};
